<template>
  <van-pull-refresh
    v-if="isSuperAdmin"
    v-model="refreshing"
    class="page-pull-refresh"
    @refresh="onRefresh"
  >
    <div class="permission-management">
      <div class="management-section">
      <div class="section-header">
        <h3>权限管理</h3>
        <span class="section-tip">仅超级管理员可维护角色权限、码头权限和页面文案配置</span>
      </div>

      <div class="permission-block">
        <div class="block-header">
          <span class="block-title">角色权限列表</span>
          <van-button
            size="small"
            type="primary"
            icon="plus"
            @click="openAddRole"
          >
            添加角色
          </van-button>
        </div>
        <div v-if="roleList.length === 0" class="empty-state">
          <van-empty description="暂无角色数据" />
        </div>
        <van-cell-group v-else inset>
          <van-cell
            v-for="role in roleList"
            :key="role.code"
            :title="role.description"
            :label="`角色编码：${role.code}`"
            is-link
            @click="openRolePermissions(role)"
          >
            <template #right-icon>
              <van-button
                size="small"
                type="primary"
                icon="setting-o"
                class="config-button"
                @click.stop="openRolePermissions(role)"
              >
                配置
              </van-button>
            </template>
          </van-cell>
        </van-cell-group>
      </div>

      <div class="permission-block">
        <div class="block-header">
          <span class="block-title">码头权限列表</span>
        </div>
        <div v-if="portList.length === 0" class="empty-state">
          <van-empty description="暂无码头数据" />
        </div>
        <van-cell-group v-else inset>
          <van-cell
            v-for="port in portList"
            :key="port.code"
            :title="port.description"
            :label="`码头代码：${port.code}`"
            is-link
            @click="openPortPermissions(port)"
          >
            <template #right-icon>
              <van-button
                size="small"
                type="primary"
                icon="setting-o"
                class="config-button"
                @click.stop="openPortPermissions(port)"
              >
                配置
              </van-button>
            </template>
          </van-cell>
        </van-cell-group>
      </div>

      <div class="permission-block">
        <div class="block-header">
          <div>
            <span class="block-title">页面文案配置</span>
            <div class="block-subtitle">按码头维护页面标题、筛选项、统计项等显示文字</div>
          </div>
        </div>
        <div v-if="uiTextPortList.length === 0" class="empty-state">
          <van-empty description="暂无可配置码头" />
        </div>
        <van-cell-group v-else inset>
          <van-cell
            v-for="port in uiTextPortList"
            :key="`ui-text-${port.code}`"
            :title="port.description"
            :label="port.code === '*' ? '默认配置：未单独配置码头时使用' : `码头代码：${port.code}`"
            is-link
            @click="openUiTexts(port)"
          >
            <template #right-icon>
              <van-button
                size="small"
                type="primary"
                icon="edit"
                class="config-button"
                @click.stop="openUiTexts(port)"
              >
                配置
              </van-button>
            </template>
          </van-cell>
        </van-cell-group>
      </div>
    </div>

    <van-popup
      v-model="showPermissionPopup"
      position="bottom"
      round
      :style="{ height: '78%' }"
      :close-on-click-overlay="!permissionSaving"
    >
      <div class="popup-header">
        <h3>{{ popupTitle }}</h3>
        <van-icon name="cross" @click="closePermissionPopup" />
      </div>

      <div v-if="permissionLoading" class="loading-wrapper">
        <van-loading size="24px" vertical>加载中...</van-loading>
      </div>

      <div v-else class="permission-popup-content">
        <div class="permission-summary">
          <div class="summary-title">{{ currentTargetName }}</div>
          <div class="summary-subtitle">勾选后保存，即表示为该{{ targetTypeLabel }}添加页面权限；取消勾选则表示删除权限。</div>
        </div>

        <div v-if="permissionOptions.length === 0" class="empty-state">
          <van-empty description="暂无可配置页面权限" />
        </div>

        <van-checkbox-group v-else v-model="selectedPermissionCodes" class="permission-list">
          <div
            v-for="item in permissionOptions"
            :key="item.code"
            class="permission-item"
            @click="togglePermission(item.code)"
          >
            <div class="permission-item-content">
              <div class="permission-name">{{ item.description }}</div>
              <div class="permission-code">{{ item.code }}</div>
            </div>
            <van-checkbox :name="item.code" @click.stop />
          </div>
        </van-checkbox-group>

        <div class="form-actions">
          <van-button
            round
            block
            type="primary"
            :loading="permissionSaving"
            @click="savePermissions"
          >
            保存权限配置
          </van-button>
        </div>
      </div>
    </van-popup>

    <van-popup
      v-model="showUiTextPopup"
      position="bottom"
      round
      :style="{ height: '86%' }"
      :close-on-click-overlay="!uiTextSaving"
    >
      <div class="popup-header">
        <h3>{{ uiTextPopupTitle }}</h3>
        <van-icon name="cross" @click="closeUiTextPopup" />
      </div>

      <div v-if="uiTextLoading" class="loading-wrapper">
        <van-loading size="24px" vertical>加载中...</van-loading>
      </div>

      <div v-else class="ui-text-popup-content">
        <div class="permission-summary">
          <div class="summary-title">{{ currentUiTextPortName }}</div>
          <div class="summary-subtitle">
            {{ currentUiTextPortCode === '*' ? '维护系统级默认文案。码头未单独配置时，将自动继承这里的值。' : '维护当前码头的页面文案覆盖值。未单独配置的项会自动继承默认配置。' }}
          </div>
        </div>

        <div class="toolbar-row">
          <van-search
            v-model="uiTextKeyword"
            class="toolbar-search"
            placeholder="搜索 key、分类、文案值"
            show-action
            @search="loadUiTextList"
            @clear="loadUiTextList"
          >
            <template #action>
              <div @click="loadUiTextList">搜索</div>
            </template>
          </van-search>
          <van-button
            v-if="currentUiTextPortCode === '*'"
            type="primary"
            size="small"
            icon="plus"
            class="toolbar-btn"
            @click="openAddUiText"
          >
            新增文案项
          </van-button>
          <van-button
            type="default"
            size="small"
            icon="down"
            class="toolbar-btn toolbar-btn--light"
            @click="exportUiTexts"
          >
            导出
          </van-button>
          <van-button
            type="default"
            size="small"
            icon="upgrade"
            class="toolbar-btn toolbar-btn--light"
            @click="triggerImportUiTexts"
          >
            导入
          </van-button>
        </div>

        <input
          ref="uiTextFileInput"
          type="file"
          accept=".json,application/json"
          class="hidden-file-input"
          @change="handleImportFile"
        >

        <div v-if="uiTextList.length === 0" class="empty-state">
          <van-empty description="暂无文案配置数据" />
        </div>

        <div v-else class="ui-text-list">
          <div
            v-for="item in uiTextList"
            :key="`${currentUiTextPortCode}-${item.textKey}`"
            class="ui-text-item"
          >
            <div class="ui-text-item__header">
              <div class="ui-text-item__title">{{ item.textKey }}</div>
              <div class="ui-text-item__tags">
                <van-tag v-if="item.category" size="small" type="primary">{{ item.category }}</van-tag>
                <van-tag v-if="item.isInherited" size="small" plain type="warning">继承默认</van-tag>
                <van-tag v-else size="small" type="success">已覆盖</van-tag>
              </div>
            </div>

            <div class="ui-text-item__value">{{ item.textValue || '-' }}</div>

            <div v-if="currentUiTextPortCode !== '*' && item.defaultTextValue" class="ui-text-item__default">
              默认值：{{ item.defaultTextValue }}
            </div>

            <div v-if="item.remark" class="ui-text-item__remark">{{ item.remark }}</div>

            <div class="ui-text-item__actions">
              <van-button size="small" type="primary" plain @click="openEditUiText(item)">编辑</van-button>
              <van-button
                v-if="currentUiTextPortCode !== '*' && !item.isInherited && item.id > 0"
                size="small"
                type="warning"
                plain
                @click="resetUiText(item)"
              >
                恢复继承
              </van-button>
              <van-button
                v-if="currentUiTextPortCode === '*' && item.id > 0"
                size="small"
                type="danger"
                plain
                @click="deleteUiText(item)"
              >
                删除
              </van-button>
            </div>
          </div>
        </div>
      </div>
    </van-popup>

    <van-popup
      v-model="showUiTextEditor"
      position="bottom"
      round
      :style="{ height: '68%' }"
      :close-on-click-overlay="!uiTextSaving"
    >
      <div class="popup-header">
        <h3>{{ uiTextEditorTitle }}</h3>
        <van-icon name="cross" @click="closeUiTextEditor" />
      </div>

      <div class="editor-content">
        <van-form @submit="saveUiText">
          <van-field
            v-model="uiTextForm.textKey"
            label="文案键"
            placeholder="例如：statistics.card.boarding"
            :readonly="!uiTextForm.isNew || currentUiTextPortCode !== '*'"
            :rules="[{ required: true, message: '请输入文案键' }]"
          />
          <van-field
            v-model="uiTextForm.textValue"
            type="textarea"
            rows="3"
            autosize
            label="文案值"
            placeholder="请输入显示文字"
            :rules="[{ required: true, message: '请输入文案值' }]"
          />
          <van-field
            v-model="uiTextForm.category"
            label="分类"
            placeholder="例如：login / home / passRecord"
          />
          <van-field
            v-model="uiTextForm.remark"
            type="textarea"
            rows="2"
            autosize
            label="备注"
            placeholder="可填写用途说明"
          />

          <div class="form-actions">
            <van-button
              round
              block
              type="primary"
              native-type="submit"
              :loading="uiTextSaving"
            >
              保存文案配置
            </van-button>
          </div>
        </van-form>
      </div>
    </van-popup>

    <van-popup
      v-model="showAddRolePopup"
      position="bottom"
      round
      :style="{ height: 'auto' }"
      :close-on-click-overlay="!addRoleSaving"
    >
      <div class="popup-header">
        <h3>添加角色</h3>
        <van-icon name="cross" @click="closeAddRole" />
      </div>

      <div class="editor-content">
        <van-form @submit="doConfirmAddRole">
          <van-field
            v-model="addRoleForm.roleName"
            label="角色名称"
            placeholder="请输入角色名称"
            :rules="[{ required: true, message: '请输入角色名称' }]"
          />
          <van-field
            v-model="addRoleForm.roleCode"
            label="角色编码"
            placeholder="请输入角色编码"
            :rules="[{ required: true, message: '请输入角色编码' }]"
          />

          <div class="form-actions form-actions--row">
            <van-button
              round
              block
              type="default"
              @click="closeAddRole"
            >
              取消
            </van-button>
            <van-button
              round
              block
              type="primary"
              native-type="submit"
              :loading="addRoleSaving"
            >
              确定
            </van-button>
          </div>
        </van-form>
      </div>
    </van-popup>
    </div>
  </van-pull-refresh>
</template>

<script>
import axios from '@/utils/request'
import globalDic, { fetchMultiDic } from '@/utils/dic'
import { loadUiTexts } from '@/utils/uiText'

export default {
  name: 'PermissionManagement',
  data() {
    return {
      refreshing: false,
      roleList: [],
      portList: [],
      uiTextPortList: [],
      showPermissionPopup: false,
      permissionLoading: false,
      permissionSaving: false,
      currentTargetType: '',
      currentTargetCode: '',
      currentTargetName: '',
      permissionOptions: [],
      selectedPermissionCodes: [],
      showUiTextPopup: false,
      showUiTextEditor: false,
      uiTextLoading: false,
      uiTextSaving: false,
      uiTextKeyword: '',
      currentUiTextPortCode: '',
      currentUiTextPortName: '',
      uiTextList: [],
      uiTextForm: this.createUiTextForm(),
      showAddRolePopup: false,
      addRoleSaving: false,
      addRoleForm: {
        roleName: '',
        roleCode: ''
      }
    }
  },
  computed: {
    currentUser() {
      return JSON.parse(localStorage.getItem('userInfo') || '{}')
    },
    isSuperAdmin() {
      return Number(this.currentUser.roleID) === 0
    },
    popupTitle() {
      if (this.currentTargetType === 'role') {
        return '配置角色页面权限'
      }
      if (this.currentTargetType === 'port') {
        return '配置码头页面权限'
      }
      return '权限配置'
    },
    targetTypeLabel() {
      return this.currentTargetType === 'role' ? '角色' : '码头'
    },
    uiTextPopupTitle() {
      return this.currentUiTextPortCode === '*' ? '维护默认页面文案' : '维护码头页面文案'
    },
    uiTextEditorTitle() {
      return this.uiTextForm.isNew ? '新增文案配置' : '编辑文案配置'
    }
  },
  mounted() {
    this.loadBaseData()
  },
  methods: {
    createUiTextForm() {
      return {
        id: 0,
        portCode: '',
        textKey: '',
        textValue: '',
        category: '',
        remark: '',
        isNew: true
      }
    },
    async loadBaseData() {
      if (!this.isSuperAdmin) {
        return
      }

      if (!globalDic.D_Role.length || !globalDic.D_Port.length) {
        await fetchMultiDic(['D_Role', 'D_Port'])
      }

      this.roleList = (globalDic.D_Role || [])
        .filter(item => item.code !== '-99')
        .slice()
        .sort((a, b) => Number(a.code) - Number(b.code))

      this.portList = (globalDic.D_Port || [])
        .filter(item => !!item.code)
        .slice()
        .sort((a, b) => String(a.code).localeCompare(String(b.code)))

      this.uiTextPortList = [
        {
          code: '*',
          description: '全局默认配置'
        }
      ].concat(this.portList)
    },
    async onRefresh() {
      try {
        await this.loadBaseData()
      } finally {
        this.refreshing = false
      }
    },
    openAddRole() {
      this.addRoleForm = {
        roleName: '',
        roleCode: ''
      }
      this.showAddRolePopup = true
    },
    closeAddRole() {
      if (this.addRoleSaving) {
        return
      }
      this.showAddRolePopup = false
    },
    doConfirmAddRole() {
      this.$dialog.confirm({
        title: '确认添加',
        message: `确认添加角色「${this.addRoleForm.roleName}」（编码：${this.addRoleForm.roleCode}）吗？`
      }).then(async () => {
        this.addRoleSaving = true
        try {
          const res = await axios.post('/api/Dic/AddDic', {
            dicName: 'D_Role',
            code: this.addRoleForm.roleCode,
            description: this.addRoleForm.roleName
          })

          if (res && res.data && res.data.success) {
            this.$toast.success('角色添加成功')
            this.showAddRolePopup = false
            await fetchMultiDic(['D_Role'])
            this.roleList = (globalDic.D_Role || [])
              .filter(item => item.code !== '-99')
              .slice()
              .sort((a, b) => Number(a.code) - Number(b.code))
          } else {
            this.$toast.fail((res && res.data && res.data.message) || '角色添加失败')
          }
        } catch (error) {
          this.$toast.fail('角色添加失败')
        } finally {
          this.addRoleSaving = false
        }
      }).catch(() => {})
    },
    async openRolePermissions(role) {
      this.currentTargetType = 'role'
      this.currentTargetCode = String(role.code)
      this.currentTargetName = `${role.description}（${role.code}）`
      await this.loadPermissions({
        url: '/api/PagePermission/QueryRolePermissions',
        payload: {
          roleId: Number(role.code)
        }
      })
    },
    async openPortPermissions(port) {
      this.currentTargetType = 'port'
      this.currentTargetCode = String(port.code)
      this.currentTargetName = `${port.description}（${port.code}）`
      await this.loadPermissions({
        url: '/api/PagePermission/QueryPortPermissions',
        payload: {
          portCode: port.code
        }
      })
    },
    async loadPermissions({ url, payload }) {
      this.permissionLoading = true
      this.showPermissionPopup = true
      this.permissionOptions = []
      this.selectedPermissionCodes = []

      try {
        const res = await axios.post(url, payload)
        if (res && res.data && res.data.success) {
          const options = (res.data.data || []).slice().sort((a, b) => a.sort - b.sort)
          this.permissionOptions = options
          this.selectedPermissionCodes = options.filter(item => item.selected).map(item => item.code)
        } else {
          this.$toast.fail((res && res.data && res.data.message) || '加载权限列表失败')
          this.showPermissionPopup = false
        }
      } catch (error) {
        this.$toast.fail('加载权限列表失败')
        this.showPermissionPopup = false
      } finally {
        this.permissionLoading = false
      }
    },
    togglePermission(code) {
      const exists = this.selectedPermissionCodes.includes(code)
      if (exists) {
        this.selectedPermissionCodes = this.selectedPermissionCodes.filter(item => item !== code)
      } else {
        this.selectedPermissionCodes = this.selectedPermissionCodes.concat(code)
      }
    },
    closePermissionPopup() {
      if (this.permissionSaving) {
        return
      }

      this.showPermissionPopup = false
    },
    async savePermissions() {
      if (!this.currentTargetType || !this.currentTargetCode) {
        this.$toast.fail('当前没有可保存的权限对象')
        return
      }

      this.permissionSaving = true
      try {
        const url = this.currentTargetType === 'role'
          ? '/api/PagePermission/SaveRolePermissions'
          : '/api/PagePermission/SavePortPermissions'

        const payload = this.currentTargetType === 'role'
          ? {
            roleId: Number(this.currentTargetCode),
            permissionCodes: this.selectedPermissionCodes
          }
          : {
            portCode: this.currentTargetCode,
            permissionCodes: this.selectedPermissionCodes
          }

        const res = await axios.post(url, payload)
        if (res && res.data && res.data.success) {
          this.$toast.success('权限配置保存成功')
          this.showPermissionPopup = false
        } else {
          this.$toast.fail((res && res.data && res.data.message) || '权限配置保存失败')
        }
      } catch (error) {
        this.$toast.fail('权限配置保存失败')
      } finally {
        this.permissionSaving = false
      }
    },
    async openUiTexts(port) {
      this.currentUiTextPortCode = String(port.code)
      this.currentUiTextPortName = `${port.description}（${port.code}）`
      this.uiTextKeyword = ''
      this.showUiTextPopup = true
      await this.loadUiTextList()
    },
    closeUiTextPopup() {
      if (this.uiTextSaving) {
        return
      }
      this.showUiTextPopup = false
    },
    async loadUiTextList() {
      if (!this.currentUiTextPortCode) {
        return
      }

      this.uiTextLoading = true
      try {
        const res = await axios.post('/api/UiTextConfig/QueryManage', {
          portCode: this.currentUiTextPortCode,
          keyword: this.uiTextKeyword
        })

        if (res && res.data && res.data.success) {
          this.uiTextList = res.data.data || []
        } else {
          this.$toast.fail((res && res.data && res.data.message) || '加载页面文案失败')
          this.uiTextList = []
        }
      } catch (error) {
        this.$toast.fail('加载页面文案失败')
        this.uiTextList = []
      } finally {
        this.uiTextLoading = false
      }
    },
    openAddUiText() {
      this.uiTextForm = {
        id: 0,
        portCode: this.currentUiTextPortCode,
        textKey: '',
        textValue: '',
        category: '',
        remark: '',
        isNew: true
      }
      this.showUiTextEditor = true
    },
    openEditUiText(item) {
      this.uiTextForm = {
        id: item.id || 0,
        portCode: this.currentUiTextPortCode,
        textKey: item.textKey,
        textValue: item.isInherited ? (item.defaultTextValue || item.textValue || '') : (item.ownTextValue || item.textValue || ''),
        category: item.category || '',
        remark: item.remark || '',
        isNew: !item.id
      }
      this.showUiTextEditor = true
    },
    closeUiTextEditor() {
      if (this.uiTextSaving) {
        return
      }
      this.showUiTextEditor = false
    },
    async saveUiText() {
      this.uiTextSaving = true
      try {
        const res = await axios.post('/api/UiTextConfig/Save', {
          id: this.uiTextForm.id,
          portCode: this.uiTextForm.portCode,
          textKey: this.uiTextForm.textKey,
          textValue: this.uiTextForm.textValue,
          category: this.uiTextForm.category,
          remark: this.uiTextForm.remark,
          state: 1
        })

        if (res && res.data && res.data.success) {
          this.$toast.success('文案配置保存成功')
          this.showUiTextEditor = false
          await this.loadUiTextList()
          await this.refreshRuntimeTextsIfNeeded()
        } else {
          this.$toast.fail((res && res.data && res.data.message) || '文案配置保存失败')
        }
      } catch (error) {
        this.$toast.fail('文案配置保存失败')
      } finally {
        this.uiTextSaving = false
      }
    },
    async deleteUiText(item) {
      this.$dialog.confirm({
        title: '确认删除',
        message: `确认删除文案键 ${item.textKey} 吗？`
      }).then(async () => {
        this.uiTextSaving = true
        try {
          const res = await axios.post('/api/UiTextConfig/Delete', {
            id: item.id
          })

          if (res && res.data && res.data.success) {
            this.$toast.success('文案配置已删除')
            if (this.showUiTextEditor && this.uiTextForm.id === item.id) {
              this.showUiTextEditor = false
            }
            await this.loadUiTextList()
            await this.refreshRuntimeTextsIfNeeded()
          } else {
            this.$toast.fail((res && res.data && res.data.message) || '文案配置删除失败')
          }
        } catch (error) {
          this.$toast.fail('文案配置删除失败')
        } finally {
          this.uiTextSaving = false
        }
      }).catch(() => {})
    },
    async resetUiText(item) {
      this.$dialog.confirm({
        title: '恢复继承',
        message: `确认删除当前码头对 ${item.textKey} 的覆盖值并恢复继承默认配置吗？`
      }).then(async () => {
        this.uiTextSaving = true
        try {
          const res = await axios.post('/api/UiTextConfig/Delete', {
            id: item.id
          })

          if (res && res.data && res.data.success) {
            this.$toast.success('已恢复继承默认值')
            await this.loadUiTextList()
            await this.refreshRuntimeTextsIfNeeded()
          } else {
            this.$toast.fail((res && res.data && res.data.message) || '恢复继承失败')
          }
        } catch (error) {
          this.$toast.fail('恢复继承失败')
        } finally {
          this.uiTextSaving = false
        }
      }).catch(() => {})
    },
    async refreshRuntimeTextsIfNeeded() {
      const currentUserPortCode = this.currentUser.portCode || ''
      if (!currentUserPortCode) {
        return
      }

      if (this.currentUiTextPortCode === '*' || this.currentUiTextPortCode === currentUserPortCode) {
        try {
          await loadUiTexts(currentUserPortCode)
        } catch (error) {
          // 忽略刷新失败，不影响管理操作
        }
      }
    },
    exportUiTexts() {
      if (!this.currentUiTextPortCode) {
        this.$toast.fail('当前没有可导出的码头配置')
        return
      }

      const exportPayload = {
        portCode: this.currentUiTextPortCode,
        portName: this.currentUiTextPortName,
        exportedAt: new Date().toISOString(),
        items: this.uiTextList.map(item => ({
          textKey: item.textKey,
          textValue: item.textValue || '',
          category: item.category || '',
          remark: item.remark || '',
          state: item.state == null ? 1 : item.state
        }))
      }

      const blob = new Blob([JSON.stringify(exportPayload, null, 2)], {
        type: 'application/json;charset=utf-8'
      })
      const fileName = `ui-text-${this.currentUiTextPortCode}-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.json`
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    },
    triggerImportUiTexts() {
      if (!this.currentUiTextPortCode) {
        this.$toast.fail('当前没有可导入的码头配置')
        return
      }

      if (this.$refs.uiTextFileInput) {
        this.$refs.uiTextFileInput.value = ''
        this.$refs.uiTextFileInput.click()
      }
    },
    async handleImportFile(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) {
        return
      }

      try {
        const text = await file.text()
        const parsed = JSON.parse(text)
        const items = Array.isArray(parsed)
          ? parsed
          : Array.isArray(parsed.items)
            ? parsed.items
            : []

        const normalizedItems = items
          .map(item => ({
            textKey: item.textKey || item.key || '',
            textValue: item.textValue || item.value || '',
            category: item.category || '',
            remark: item.remark || '',
            state: item.state == null ? 1 : Number(item.state)
          }))
          .filter(item => item.textKey && item.textValue)

        if (normalizedItems.length === 0) {
          this.$toast.fail('导入文件中没有有效文案数据')
          return
        }

        await this.$dialog.confirm({
          title: '确认导入',
          message: `将向 ${this.currentUiTextPortName} 批量导入 ${normalizedItems.length} 条文案配置，已存在的同 key 文案会被覆盖。是否继续？`
        })

        this.uiTextSaving = true
        const res = await axios.post('/api/UiTextConfig/Import', {
          portCode: this.currentUiTextPortCode,
          items: normalizedItems
        })

        if (res && res.data && res.data.success) {
          this.$toast.success(res.data.message || '文案配置导入成功')
          await this.loadUiTextList()
          await this.refreshRuntimeTextsIfNeeded()
        } else {
          this.$toast.fail((res && res.data && res.data.message) || '文案配置导入失败')
        }
      } catch (error) {
        this.$toast.fail('导入文件解析失败，请确认 JSON 格式正确')
      } finally {
        this.uiTextSaving = false
        if (this.$refs.uiTextFileInput) {
          this.$refs.uiTextFileInput.value = ''
        }
      }
    }
  }
}
</script>

<style scoped>
.permission-management {
  margin-top: 16px;
}

.management-section {
  background: white;
  border-radius: 12px;
  padding: 16px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 12px;
}

.section-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #323233;
}

.section-tip {
  color: #969799;
  font-size: 12px;
  text-align: right;
}

.permission-block + .permission-block {
  margin-top: 20px;
}

.block-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.block-title {
  font-size: 15px;
  font-weight: 600;
  color: #323233;
}

.block-subtitle {
  margin-top: 4px;
  font-size: 12px;
  color: #969799;
}

.config-button {
  min-width: 88px;
  height: 34px;
  color: #fff !important;
}

.permission-block :deep(.van-cell) {
  align-items: center;
}

.permission-block :deep(.van-cell__title) {
  flex: 1 1 auto;
  min-width: 0;
}

.permission-block :deep(.van-cell__label) {
  word-break: break-all;
}

.permission-block :deep(.van-cell__right-icon) {
  margin-left: 8px;
}

.config-button :deep(.van-button__text),
.config-button :deep(.van-icon) {
  color: #fff !important;
}

.empty-state {
  padding: 20px 0;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #ebedf0;
}

.popup-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
}

.loading-wrapper {
  height: calc(100% - 56px);
  display: flex;
  justify-content: center;
  align-items: center;
}

.permission-popup-content,
.ui-text-popup-content {
  height: calc(100% - 56px);
  display: flex;
  flex-direction: column;
  padding: 16px;
}

.permission-summary {
  background: #f7f8fa;
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 16px;
}

.summary-title {
  font-size: 15px;
  font-weight: 600;
  color: #323233;
}

.summary-subtitle {
  margin-top: 6px;
  font-size: 13px;
  line-height: 1.5;
  color: #646566;
}

.permission-list {
  flex: 1;
  overflow-y: auto;
}

.permission-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 12px;
  border: 1px solid #ebedf0;
  border-radius: 10px;
  margin-bottom: 10px;
  background: #fff;
}

.permission-item-content {
  flex: 1;
  min-width: 0;
  margin-right: 12px;
}

.permission-name {
  font-size: 15px;
  font-weight: 600;
  color: #323233;
}

.permission-code {
  margin-top: 4px;
  font-size: 12px;
  color: #969799;
  word-break: break-all;
}

.toolbar-row {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
}

.toolbar-search {
  flex: 1;
}

.toolbar-btn {
  flex-shrink: 0;
}

.toolbar-btn--light {
  min-width: 76px;
}

.ui-text-list {
  flex: 1;
  overflow-y: auto;
}

.ui-text-item {
  padding: 14px;
  border: 1px solid #ebedf0;
  border-radius: 12px;
  background: #fff;
}

.ui-text-item + .ui-text-item {
  margin-top: 12px;
}

.ui-text-item__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.ui-text-item__title {
  font-size: 14px;
  font-weight: 700;
  color: #323233;
  word-break: break-all;
}

.ui-text-item__tags {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.ui-text-item__value {
  margin-top: 10px;
  font-size: 15px;
  line-height: 1.6;
  color: #323233;
  word-break: break-all;
}

.ui-text-item__default,
.ui-text-item__remark {
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.5;
  color: #969799;
}

.ui-text-item__actions {
  margin-top: 12px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.editor-content {
  height: calc(100% - 56px);
  overflow-y: auto;
  padding: 16px;
}

.hidden-file-input {
  display: none;
}

.form-actions {
  margin-top: 16px;
}

.form-actions--row {
  display: flex;
  gap: 12px;
}

.form-actions--row .van-button {
  flex: 1;
}

@media (max-width: 767px) {
  .section-header,
  .toolbar-row,
  .ui-text-item__header {
    flex-direction: column;
    align-items: stretch;
  }

  .toolbar-btn {
    width: 100%;
  }

  .config-button {
    width: auto;
    min-width: 72px;
  }

  .permission-block :deep(.van-cell) {
    padding: 12px 14px;
  }

  .permission-block :deep(.van-cell__right-icon) {
    flex: 0 0 auto;
    margin-left: 10px;
  }

  .ui-text-item__tags {
    justify-content: flex-start;
  }
}

@media (prefers-color-scheme: dark) {
  .management-section {
    background: #2a2a2a;
  }

  .section-header h3,
  .block-title,
  .popup-header h3,
  .summary-title,
  .permission-name,
  .ui-text-item__title,
  .ui-text-item__value {
    color: #fff;
  }

  .section-tip,
  .block-subtitle,
  .permission-code,
  .summary-subtitle,
  .ui-text-item__default,
  .ui-text-item__remark {
    color: #ccc;
  }

  .popup-header {
    border-bottom-color: #444;
  }

  .permission-summary,
  .permission-item,
  .ui-text-item {
    background: #333;
    border-color: #444;
  }
}
</style>
